import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../../context/Auth';
import { toast } from 'react-toastify';
import ExportData from '../../../components/common/ExportData';
import SortableTableHeader from '../../../components/Table/SortableTableHeader';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  FileText,
} from 'lucide-react';

const PrintRequisitionReport = () => {
  const [searchParams] = useSearchParams();

  const branch = searchParams.get("branch");
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const requestType = searchParams.get("type");
  const { requestWithAuth } = useAuth();

  const [loading, setLoading] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const sortParam = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : "";

  const fetchRequisitions = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/report/requisition?page=${current_page}&pageSize=${page_size}&search=${search_query}&branch=${branch}&startDate=${startDate}&endDate=${endDate}&type=${requestType}${sortParam}`,
        "GET"
      );
      setRequisitions(data?.results || []);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil((data?.count || 0) / page_size));
    } catch (error) {
      setRequisitions([]);
      toast.error(error?.message || "Failed to load requisition report data");
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (search_query) => {
    try {
      const data = await requestWithAuth(
        `/report/requisition?page=1&pageSize=1000000&search=${search_query}&branch=${branch}&startDate=${startDate}&endDate=${endDate}&type=${requestType}${sortParam}`,
        "GET"
      );
      const formatted = data?.results?.map(item => {
        const row = {
          "Description": item?.drug || "",
          "Requested By": item?.requested_by || "",
          "Branch": item?.branch?.name || "",
          "Quantity Requested": item?.quantity || 0,
        };
        if (requestType === "supplied") {
          row["Quantity Supplied"] = item?.quantity_supplied || 0;
          row["Supplied By"] = item?.supplied_by || "";
        }
        if (requestType === "received") {
          row["Quantity Supplied"] = item?.quantity_supplied || 0;
          row["Received By"] = item?.approved_by || "";
        }
        row["Time Requested"] = formatDate(item?.created_at) || "";
        if (requestType === "supplied") {
          row["Time Supplied"] = formatDate(item?.date_supplied) || "";
        }
        if (requestType === "received") {
          row["Time Received"] = formatDate(item?.date_approved) || "";
        }
        return row;
      });
      setTableData(formatted || []);
    } catch (error) {
      console.error("Error fetching export data:", error);
      setTableData([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filterRequisitions = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1); // Reset to page 1 whenever search query changes
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3; // Number of pages to show around the current page

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    // Always show the first page
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    // Show the numbers around the current page
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Always show the last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchRequisitions(pageSize, search, currentPage);
    getTableData(search);
  }, [search, currentPage, pageSize, branch, startDate, endDate, requestType, sortBy, sortOrder]);

  const getReportTitle = () => {
    if (requestType === "supplied") return "Warehouse Supply Report";
    if (requestType === "request") return "Inventory Request Report";
    if (requestType === "received") return "Branch Receipt Report";
    return "Requisition Report";
  };

  const getBackLink = () => {
    if (requestType === "request") return "/report/inventory/request";
    if (requestType === "supplied") return "/report/inventory/supply";
    if (requestType === "received") return "/report/inventory/receipt";
    return "/";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Reports</span>
                <span>/</span>
                <span>Requisition</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  {getReportTitle()}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText size={32} className="text-blue-600" />
                {getReportTitle()}
              </h1>
            </div>

            <Link
              to={getBackLink()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors shadow-sm"
            >
              <ChevronLeft size={16} />
              Back to Criteria
            </Link>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">

          {/* Controls / Filter Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName={getReportTitle()}
                />

                <span className="text-sm text-gray-600 ml-2">Show</span>
                <select
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  value={pageSize}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              {startDate && endDate && (
                <div className="text-sm text-gray-600">
                  Period: <span className="font-semibold">{startDate}</span> to{" "}
                  <span className="font-semibold">{endDate}</span>
                </div>
              )}

              <div className="w-72 relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search description..."
                  value={search}
                  onChange={(e) => filterRequisitions(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Table Area */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading requisition report data...</span>
                </div>
              </div>
            ) : requisitions?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <SortableTableHeader
                      label="Description"
                      fieldKey="drug"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Requested By"
                      fieldKey="requested_by"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch
                    </th>
                    <SortableTableHeader
                      label="Quantity Requested"
                      fieldKey="quantity"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    {requestType === "supplied" && (
                      <>
                        <SortableTableHeader
                          label="Quantity Supplied"
                          fieldKey="quantity_supplied"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                        <SortableTableHeader
                          label="Supplied By"
                          fieldKey="supplied_by"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                      </>
                    )}
                    {requestType === "received" && (
                      <>
                        <SortableTableHeader
                          label="Quantity Supplied"
                          fieldKey="quantity_supplied"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Received By
                        </th>
                      </>
                    )}
                    <SortableTableHeader
                      label="Time Requested"
                      fieldKey="created_at"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    {requestType === "supplied" && (
                      <SortableTableHeader
                        label="Time Supplied"
                        fieldKey="date_supplied"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      />
                    )}
                    {requestType === "received" && (
                      <SortableTableHeader
                        label="Time Received"
                        fieldKey="date_approved"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      />
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {requisitions?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.drug}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.requested_by}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.branch?.name}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.quantity}
                      </td>
                      {requestType === "supplied" && (
                        <>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.quantity_supplied}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.supplied_by}
                          </td>
                        </>
                      )}
                      {requestType === "received" && (
                        <>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.quantity_supplied}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {item?.approved_by}
                          </td>
                        </>
                      )}
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {formatDate(item?.created_at)}
                      </td>
                      {requestType === "supplied" && (
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {formatDate(item?.date_supplied)}
                        </td>
                      )}
                      {requestType === "received" && (
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {formatDate(item?.date_approved)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <FileText className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm font-medium mb-1">
                  No Data Available
                </p>
                <p className="text-gray-400 text-xs">
                  Adjust your search or filter parameters to see results
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {requisitions?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                        ? "text-gray-600 hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintRequisitionReport;